<template>
    <div class="flex flex-col">
        <div
            class="flex items-center justify-between border-b-gray-500 bg-white"
            v-on:click="toggleGoals"
        >
            <div class="flex items-center md:w-full">
                <p
                    class="w-full select-none whitespace-nowrap py-2 pr-3 text-left text-[15px] font-semibold !text-gray-950 md:bg-gray-250 lg:mb-2"
                >
                    Classroom Goals
                </p>
                <p
                    class="ml-4 whitespace-nowrap rounded bg-gray-350 px-2 py-1 text-[13px] text-gray-850 md:hidden"
                >
                    {{ activeGoalsCount }} active
                </p>
            </div>
            <div
                :class="
                    'flex cursor-pointer items-center p-4 md:hidden' +
                    ' ' +
                    (showGoals
                        ? 'transform:rotate(0deg)'
                        : 'transform:rotate(180deg)')
                "
            >
                <inline-svg
                    src="/assets/art/chevron_down.svg"
                    class="inline-block fill-current text-gray-550"
                ></inline-svg>
            </div>
        </div>
        <div
            :class="
                'mt-4 flex flex-col gap-4 pb-4' +
                ' ' +
                (showGoals ? '' : 'hidden md:block')
            "
        >
            <div v-for="(goal, index) in goals">
                <div
                    class="flex w-full flex-wrap items-center gap-1 whitespace-nowrap"
                >
                    <div
                        class="text-medium order-1 mr-4 flex h-10 w-10 flex-none items-center justify-center rounded rounded-full bg-blue-300 text-[15px] text-white"
                    >
                        {{ goal.label }}
                    </div>
                    <div
                        class="order-3 flex w-full flex-none items-center gap-2 md:order-2 md:w-auto md:flex-grow"
                    >
                        <div>{{ goal.prefix }}</div>
                        <div class="w-14 lg:w-auto lg:max-w-[4rem] lg:mr-2">
                            <form-input-number
                                v-model="form.goals[index].target"
                                wrapper-css-classes="inline"
                                placeholder="0"
                                type="number"
                                css-classes="!pr-2"
                                :min="0"
                                :max="9999"
                                :disabled="classroom.archived_at ? true : false"
                            />
                        </div>
                        <div class="">{{ goal.suffix }}</div>
                        <div class="lg:mr-2">
                            <div
                                v-if="classroom.archived_at"
                                class="rounded border border-gray-250 bg-gray-100 px-3 py-2"
                            >
                                <span
                                    >{{
                                        dayjs(form.goals[index].startingFrom)
                                            .utc(true)
                                            .tz($page.props.display_timezone)
                                            .format(
                                                $page.props.date_formats
                                                    .short_nice
                                            )
                                    }}
                                </span>
                            </div>
                            <GroupClassroomGoalStartingDateSelector
                                v-else
                                :goal-id="1"
                                :classroom="classroom"
                                v-model="form.goals[index].startingFrom"
                            />
                        </div>
                    </div>
                    <div class="order-2 flex flex-grow justify-end md:order-3">
                        <div
                            v-if="classroom.archived_at"
                            class="flex grow flex-row items-center justify-end"
                        >
                            <label class="relative inline-flex items-center">
                                <div
                                    v-if="form.goals[index].enabled"
                                    class="peer h-6 w-11 rounded-full bg-blue-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:translate-x-full after:rounded-full after:border after:border-gray-550 after:border-white after:bg-white after:transition-all after:content-[''] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"
                                ></div>
                                <div
                                    v-else
                                    class="peer h-6 w-11 rounded-full bg-gray-550 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-550 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"
                                ></div>
                            </label>
                        </div>
                        <div
                            v-else
                            class="flex grow flex-row items-center justify-end"
                        >
                            <label
                                class="relative inline-flex cursor-pointer items-center"
                            >
                                <input
                                    type="checkbox"
                                    v-model="form.goals[index].enabled"
                                    :disabled="!form.goals[index] || !form.goals[index].target || parseInt(form.goals[index].target) < 1"
                                    class="peer sr-only"
                                />
                                <div
                                    class="peer h-6 w-11 rounded-full bg-gray-550 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-550 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"
                                ></div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="mt-4 flex w-full justify-end gap-4" v-if="form.isDirty">
        <span class="btn btn-outline" @click="cancel"> Cancel </span>
        <span class="btn btn-primary" @click="updateGoals"> Update Goals </span>
    </div>
</template>
<script>
import FormInputDatepicker from "@/Components/FormInputDatepicker";
import FormInputNumber from "@/Components/FormInputNumber";
import GroupClassroomGoalStartingDateSelector from "@/Components/GroupClassroomGoalStartingDateSelector";

export default {
    name: "GroupClassroomManageGoalsTable",
    components: {
        GroupClassroomGoalStartingDateSelector,
        FormInputDatepicker,
        FormInputNumber,
    },
    props: {
        classroom: {
            type: Object,
            default: {},
        },
    },
    data() {
        return {
            activeGoalsCount: 0,
            goals: [
                {
                    label: "A",
                    prefix: "Study for",
                    suffix: "hours from",
                },
                {
                    label: "B",
                    prefix: "Answer",
                    suffix: "questions from",
                },
            ],
            form: this.$inertia.form({
                goals: this.classroom.goals,
            }),
            showGoals: true,
        };
    },
    methods: {
        updateGoals() {
            if (this.classroom.archived_at) {
                return;
            }
            let vm = this;
            this.$inertia.post(
                route("group.classroom.goals.update", {
                    classroom: this.classroom.id,
                }),
                this.form,
                {
                    onSuccess() {
                        window.location.reload();
                    },
                }
            );
        },
        cancel() {
            this.form.reset();
        },
        toggleGoals() {
            this.showGoals = !this.showGoals;
        },
    },
    mounted() {
        for (let goal of this.classroom.goals) {
            if (goal.enabled) {
                this.activeGoalsCount++;
            }
        }
    },
};
</script>
